import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface AvatarTooltipProps {
  label?: string | null;
  position?: 'top' | 'bottom';
  className?: string;
  children: React.ReactNode;
}

export const AvatarTooltip: React.FC<AvatarTooltipProps> = ({
  label,
  position = 'bottom',
  className = '',
  children
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLSpanElement | null>(null);
  const [horizontalShift, setHorizontalShift] = useState(0);
  const [isActive, setIsActive] = useState(false);

  const updatePosition = useCallback(() => {
    if (!label || !tooltipRef.current) {
      setHorizontalShift(0);
      return;
    }
    const rect = tooltipRef.current.getBoundingClientRect();
    const viewportPadding = 8;
    let shift = 0;
    if (rect.right > window.innerWidth - viewportPadding) {
      shift = window.innerWidth - viewportPadding - rect.right;
    } else if (rect.left < viewportPadding) {
      shift = viewportPadding - rect.left;
    }
    setHorizontalShift(prev => (Math.abs(prev - shift) < 0.5 ? prev : shift));
  }, [label]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, position]);

  useEffect(() => {
    if (!label) {
      setHorizontalShift(0);
      return;
    }
    const handleWindowChange = () => updatePosition();
    window.addEventListener('resize', handleWindowChange);
    window.addEventListener('scroll', handleWindowChange, true);
    return () => {
      window.removeEventListener('resize', handleWindowChange);
      window.removeEventListener('scroll', handleWindowChange, true);
    };
  }, [label, updatePosition]);

  const handleTrigger = () => updatePosition();

  const showTooltip = () => {
    if (!label) return;
    setIsActive(true);
    handleTrigger();
  };

  const hideTooltip = () => {
    setIsActive(false);
  };

  useEffect(() => {
    if (!label) {
      setIsActive(false);
    }
  }, [label]);

  const positionClasses =
    position === 'top'
      ? 'bottom-full mb-1 left-1/2 -translate-x-1/2'
      : 'top-full mt-1 left-1/2 -translate-x-1/2';

  return (
    <div
      ref={wrapperRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      {children}
      {label ? (
        <span
          ref={tooltipRef}
          role="tooltip"
          className={`pointer-events-none absolute ${positionClasses} rounded-md bg-gray-900 px-2 py-1 text-[11px] font-medium text-white transition-opacity duration-150 ease-out whitespace-nowrap z-[99999] ${isActive ? 'opacity-100' : 'opacity-0'}`}
          style={{ 
            marginLeft: horizontalShift,
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)'
          }}
        >
          {label}
        </span>

      ) : null}
    </div>
  );
};

export default AvatarTooltip;

